import React from 'react';
import { EntityLayout, EntitySwitch } from '@backstage/plugin-catalog';
import { RTLLinksCard } from '../../cards/LinksCard';
import { ContactChannelsCard } from '../../cards/ContactChannelsCard';
import { Grid } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { WarningCard } from '../../cards/WarningCard';
import { RTLCatalogGraphCard } from '../../cards/CatalogGraphCard';
import { RTLAboutCard } from '../../cards/AboutCard';
import { HasFilteredResourcesCard } from '../../cards/HasFilteredResourcesCard';
import {
  hasCloudAccounts,
  hasContactChannels,
  hasCustomGroups,
  hasKubernetes,
  isNotAdGroup,
} from '../../cards/utils';
import { UpdateNamespacesCard } from '../../cards/UpdateNamespacesCard';
import { EntraGroupsGrid } from '../GroupPage/MemberListCard';
import { EntraGroupsIn } from '../../../hooks';

export const ProductPage = () => {
  const { entity } = useEntity();
  const hasClusters = (entity.spec?.data as any)?.clusters?.length > 0;

  const cmdbGroups = (entity.spec as any)?.cmdbGroups as
    | EntraGroupsIn
    | undefined;
  // The CMDB names the product directory after this, and the model api needs
  // both to address the group file - see GroupMembershipTarget.
  const productKey =
    entity.metadata.annotations?.['schema.dp.netrtl.com/legacy-short-key'];
  const teamUID = entity.spec?.ownerUid as string | undefined;
  const teamShortID = entity.spec?.ownerName as string | undefined;

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3} alignItems="stretch">
          <WarningCard />
          <Grid item md={6} sm={12}>
            {/*
              A product is declared in the CMDB, and the CMDB is already
              among the links of the entity, so "View Source" would be the
              second way to the same page - see showViewSource.
            */}
            <RTLAboutCard showViewSource={false} />
          </Grid>
          <Grid item md={6} sm={12}>
            {/*
              The graph is left with the relations of the Backstage model, so
              it shows what a product stands in relation to - its team, the
              systems - and not what it runs. The `hosts` relation would hang
              every cluster, slice, project and account off the root at once,
              a star of a dozen leaves that says nothing; those are listed in
              the Kubernetes and Cloud Accounts tabs instead. Entra groups are
              left out as on the team and the department page, see
              isNotAdGroup.
            */}
            <RTLCatalogGraphCard height={400} entityFilter={isNotAdGroup} />
          </Grid>
          <RTLLinksCard />
          <Grid item xs={12}>
            <EntitySwitch>
              <EntitySwitch.Case if={hasContactChannels}>
                <Grid item xs={12}>
                  <ContactChannelsCard />
                </Grid>
              </EntitySwitch.Case>
            </EntitySwitch>
          </Grid>
        </Grid>
      </EntityLayout.Route>
      {/*
        The Entra groups of the product and who is in them, the same tab the
        team and the department page show, see EntraGroupsGrid.

        Optional, unlike on those two: a team and a department have the
        technical pair whether they declare it or not, while a product has only
        the groups it asked for itself - and all but a handful asked for none,
        for which this would be an empty page.
      */}
      <EntityLayout.Route path="/groups" title="Groups" if={hasCustomGroups}>
        <EntraGroupsGrid
          groups={cmdbGroups}
          target={{
            kind: 'product',
            cmdbTeamUID: teamUID!,
            cmdbTeamShortID: teamShortID!,
            cmdbProductKey: productKey!,
          }}
        />
      </EntityLayout.Route>
      {hasClusters && (
        <EntityLayout.Route path="/namespaces" title="Kubernetes Namespaces">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <UpdateNamespacesCard />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}
      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={hasKubernetes}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HasFilteredResourcesCard
              type="k8s-cluster"
              title="Kubernetes Clusters"
            />
          </Grid>
          <Grid item xs={12}>
            {/*
              A cluster and a slice of it carry the same name in dev and in
              prod, which is what the Environment column of the table is for.
            */}
            <HasFilteredResourcesCard
              type="k8s-cluster-slice"
              title="Kubernetes Cluster Slices"
            />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/cloud"
        title="Cloud Accounts"
        if={hasCloudAccounts}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HasFilteredResourcesCard type="gcp-project" title="GCP Projects" />
          </Grid>
          <Grid item xs={12}>
            <HasFilteredResourcesCard type="aws-account" title="AWS Accounts" />
          </Grid>
          <Grid item xs={12}>
            <HasFilteredResourcesCard
              type="azure-subscription"
              title="Azure Subscriptions"
            />
          </Grid>
          <Grid item xs={12}>
            <HasFilteredResourcesCard type="oci-account" title="OCI Accounts" />
          </Grid>
        </Grid>
      </EntityLayout.Route>
    </EntityLayout>
  );
};
